/**
 * Prints a visitor badge from the browser on the computer that has the
 * LabelWriter plugged in. The Car-Pool server cannot reach that printer.
 *
 * The official DYMO Connect framework hardcodes a service protocol. This Mac's
 * DYMO.WebApi.Mac.Host answers plain HTTP on 127.0.0.1 ports 41951-41960.
 * The protocol is detected from the service that actually answers, then applied
 * to the vendored framework before it runs.
 */

import { VISITOR_BADGE_LABEL_XML, badgeTextFields } from './visitorBadgeLabel';

export const BADGE_STOCK_SKU = '30857';
export const PRINTER_MISSING_MESSAGE = 'DYMO Connect has to be installed on this computer, and the LabelWriter has to be plugged into it. The visit is saved.';
export const PRINT_SENT_MESSAGE = 'Print request sent to the LabelWriter on this computer. Look at the printer to confirm the badge came out. Physical printing was not confirmed.';

const SERVICE_HOST = '127.0.0.1';
const FIRST_PORT = 41951;
const LAST_PORT = 41960;

function unavailable(message = PRINTER_MISSING_MESSAGE) {
  return {
    available: false,
    accepted: false,
    confirmed: false,
    previewDataUrl: '',
    message,
  };
}

function rejected(message) {
  return {
    available: true,
    accepted: false,
    confirmed: false,
    previewDataUrl: '',
    message,
  };
}

export function rewriteFrameworkProtocol(source, protocol) {
  const pattern = /var WS_PROTOCOL="https?:\/\/"/;
  if (!pattern.test(source)) {
    throw new Error('DYMO framework is missing its service protocol setting.');
  }
  return source.replace(pattern, `var WS_PROTOCOL="${protocol}"`);
}

export function chooseDymoPrinter(printers) {
  const list = Array.isArray(printers) ? printers.filter(Boolean) : [];
  const labelWriter = list.find((printer) => /labelwriter\s*550/i.test(`${printer.name || ''} ${printer.modelName || ''}`));
  return labelWriter || list[0] || null;
}

export function readDymoPrinterList(xml) {
  const blocks = String(xml || '').match(/<LabelWriterPrinter>[\s\S]*?<\/LabelWriterPrinter>/gi) || [];
  return blocks.map((block) => ({
    name: (block.match(/<Name>([^<]*)<\/Name>/i) || [])[1] || '',
    modelName: (block.match(/<ModelName>([^<]*)<\/ModelName>/i) || [])[1] || '',
    isConnected: /<IsConnected>\s*true\s*<\/IsConnected>/i.test(block),
  })).filter((printer) => printer.name);
}

export async function readBadgePrinterStatus({
  fetchImpl = browserFetch(),
  detectService = detectDymoService,
} = {}) {
  const missing = { connected: false, name: '' };
  if (typeof fetchImpl !== 'function') return missing;
  try {
    const service = await detectService(fetchImpl);
    if (!service?.protocol || !service.host || !service.port) return missing;
    const response = await fetchImpl(
      `${service.protocol}${service.host}:${service.port}/DYMO/DLS/Printing/GetPrinters`,
    );
    if (!response?.ok) return missing;
    const printers = readDymoPrinterList(await response.text());
    const printer = printers.find((item) => /labelwriter/i.test(`${item.name} ${item.modelName}`));
    if (!printer?.name) return missing;
    return { connected: true, name: printer.name };
  } catch (error) {
    return missing;
  }
}

export function consumableSku(info) {
  return String(info?.sku || info?.SKU || info?.Sku || '').trim();
}

/**
 * The LabelWriter 550 often answers that a valid roll is loaded and leaves
 * the product number blank. That is the same roll, not a different one.
 * A real different product number is the only reading that blocks printing.
 */
export function badgeStockDecision(info) {
  const sku = consumableSku(info);
  if (sku === BADGE_STOCK_SKU) return 'match';
  if (sku) return 'mismatch';
  const status = String(info?.rollStatus || info?.RollStatus || '');
  if (/valid roll/i.test(status) && !/invalid|no roll|not loaded|empty/i.test(status)) {
    return 'unread';
  }
  return 'unknown';
}

async function probePort(fetchImpl, protocol, port) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 800);
  try {
    const response = await fetchImpl(
      `${protocol}://${SERVICE_HOST}:${port}/DYMO/DLS/Printing/StatusConnected`,
      { signal: controller.signal },
    );
    const body = await response.text();
    if (response.ok && /true/i.test(body)) {
      return { protocol: `${protocol}://`, host: SERVICE_HOST, port };
    }
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timer);
  }
  return null;
}

async function probeProtocol(fetchImpl, protocol) {
  const checks = [];
  for (let port = FIRST_PORT; port <= LAST_PORT; port += 1) {
    checks.push(probePort(fetchImpl, protocol, port));
  }
  const found = (await Promise.all(checks)).find(Boolean);
  return found || null;
}

function browserFetch() {
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    return window.fetch.bind(window);
  }
  return null;
}

export async function detectDymoService(fetchImpl = browserFetch()) {
  if (typeof fetchImpl !== 'function') return null;
  const httpService = await probeProtocol(fetchImpl, 'http');
  if (httpService) return httpService;
  return probeProtocol(fetchImpl, 'https');
}

let frameworkPromise = null;
let loadedProtocol = '';

export function loadDymoFramework({
  protocol,
  fetchImpl = browserFetch(),
  documentRef = typeof document !== 'undefined' ? document : null,
} = {}) {
  if (!documentRef) {
    return Promise.reject(new Error('DYMO printing only runs in the browser on this computer.'));
  }
  if (frameworkPromise && loadedProtocol === protocol) return frameworkPromise;
  loadedProtocol = protocol;
  frameworkPromise = (async () => {
    const response = await fetchImpl(`${process.env.PUBLIC_URL || ''}/dymo/dymo.connect.framework.js`);
    if (!response.ok) throw new Error('The DYMO printing library could not be loaded.');
    const source = rewriteFrameworkProtocol(await response.text(), protocol);
    const script = documentRef.createElement('script');
    script.text = source;
    documentRef.head.appendChild(script);
    const framework = documentRef.defaultView?.dymo?.label?.framework;
    if (!framework) throw new Error('The DYMO printing library did not start.');
    await new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(PRINTER_MISSING_MESSAGE)), 4000);
      framework.init(() => {
        clearTimeout(timer);
        resolve();
      });
    });
    return framework;
  })().catch((error) => {
    frameworkPromise = null;
    loadedProtocol = '';
    throw error;
  });
  return frameworkPromise;
}

export function portraitBase64(value) {
  const text = String(value || '');
  const splitAt = text.indexOf(',');
  if (!text.startsWith('data:image/') || splitAt < 0) return '';
  return text.slice(splitAt + 1);
}

function applyBadgeText(label, badge) {
  const fields = badgeTextFields(badge);
  Object.entries(fields).forEach(([name, value]) => {
    label.setObjectText(name, value);
  });
  const photo = portraitBase64(badge.portraitDataUrl);
  if (photo) label.setObjectText('PHOTO', photo);
  return fields;
}

export async function printDymoVisitorBadge(badge, dependencies = {}) {
  const detectService = dependencies.detectService || detectDymoService;
  const loadFramework = dependencies.loadFramework || loadDymoFramework;
  const labelXml = dependencies.labelXml || VISITOR_BADGE_LABEL_XML;

  try {
    const service = await detectService();
    if (!service) return unavailable();

    const framework = await loadFramework(service);
    const environment = framework.checkEnvironment();
    if (!environment?.isWebServicePresent) return unavailable();

    const printers = await framework.getPrintersAsync();
    const printer = chooseDymoPrinter(printers);
    if (!printer?.name) {
      return unavailable('No LabelWriter was found. Plug it into this computer and try again. The visit is saved.');
    }

    let consumable;
    try {
      consumable = await framework.getConsumableInfoIn550PrinterAsync(printer.name);
    } catch (error) {
      return rejected('The label roll could not be read. Load DYMO 30857 name badges and try again.');
    }

    const stock = badgeStockDecision(consumable);
    if (stock === 'mismatch') {
      const loaded = consumable?.name || 'a different roll';
      return rejected(`This printer has ${loaded} (${consumableSku(consumable)}) loaded. Load DYMO 30857 name badges and try again.`);
    }
    if (stock === 'unknown') {
      return rejected('The LabelWriter did not report the label roll. Seat the DYMO 30857 name badges and try again.');
    }

    const label = framework.openLabelXml(labelXml);
    if (!label?.isDCDLabel?.()) {
      return rejected('The badge template was not accepted by DYMO Connect.');
    }

    applyBadgeText(label, badge);
    const preview = label.render('', printer.name);
    if (!preview) {
      return rejected('The printer did not return a badge preview, so nothing was sent to print.');
    }

    label.print(printer.name, '', '');
    return {
      available: true,
      accepted: true,
      confirmed: false,
      previewDataUrl: `data:image/png;base64,${preview}`,
      message: PRINT_SENT_MESSAGE,
    };
  } catch (error) {
    const message = error?.message || 'The printer did not accept the badge.';
    if (/DYMO Connect has to be installed|No LabelWriter was found/i.test(message)) {
      return unavailable(message);
    }
    return rejected(message);
  }
}
